import SpineWrapper from '@Core/Animation/SpineWrapper';
import { GameScene } from '@Core/GameScene/GameScene';
import { GameStore } from '@GameStore/GameStore';
import { EGameStoreKeys } from '@GameStore/GameStoreSchema';
import { IMergeConfiguration } from '@GFTypes/GameStoreTypes';
import { ELayeringOrder } from '@GFTypes/LayeringOrder';
import EventsCenter from '@Managers/Events/EventsCenter';
import { Logger, LoggerLevel, LoggerSource } from '@Managers/Logger';
import { EGameStates } from '@Managers/StateManager/GameStates';
import { BitmapText, Container } from 'pixi.js';
import { EWildWheelFeatureStates } from './States/WildWheelFeatureStates';
import { WildWheelFeature } from './WildWheelFeature';
import gsap from 'gsap';

export class WildWheelMerge {
    public scene: GameScene;
    public spine: SpineWrapper;
    private readonly gameStore: GameStore;
    public tween: gsap.core.Tween;
    public mergeConfig: IMergeConfiguration;
    private readonly manager: WildWheelFeature;
    private multiplierContainer: Container;
    private multiplierText: BitmapText;

    constructor(scene: GameScene, manager: WildWheelFeature) {
        this.scene = scene;
        this.manager = manager;
        this.gameStore = manager.gameStore;
        Logger.log(LoggerLevel.Constructor, { class: WildWheelMerge.name, source: LoggerSource.Game, message: 'WildWheelMerge constructor' });
        EventsCenter.on(EGameStates.IDLE_STATE, this.onIdle, this);
        EventsCenter.on(EWildWheelFeatureStates.WILD_WHEEL_MERGE_START, this.start, this);
        EventsCenter.on(EWildWheelFeatureStates.WILD_WHEEL_MERGE_END, this.end, this);
        EventsCenter.on(EWildWheelFeatureStates.WILD_WHEEL_HIDDEN, this.hidden, this);

        this.mergeConfig = this.gameStore.get(EGameStoreKeys.WildWheelConfiguration).merge!;
        this.onCreate();
    }

    public onCreate(): void {
        const { spineName, skinName, placeholderName, fontName, fontSize } = this.mergeConfig;
        this.spine = new SpineWrapper(
            this.scene,
            {
                x: this.scene.screen.width / 2,
                y: this.scene.screen.height / 2
            },
            { dataKey: spineName, skinName },
            true,
            false
        )!;

        this.multiplierContainer = new Container();

        this.multiplierText = new BitmapText({
            text: '',
            style: {
                fontFamily: fontName,
                fontSize
            }
        });

        this.multiplierText.anchor.set(0.5, 1);

        this.multiplierContainer.addChild(this.multiplierText);
        this.scene.stage.addChild(this.multiplierContainer);

        this.multiplierContainer.addChild(this.multiplierText);
        this.multiplierContainer.zIndex = ELayeringOrder.ODOMETER;
        this.spine.slotManager.assignObjectToSlot(placeholderName, this.multiplierContainer);

        this.spine.spine.visible = false;

        this.spine.eventManager.registerBlocker('ensureWinWithMultiplier', () => this.manager.isWinStateWithoutWheel.bind(this.manager)());
        this.spine.eventManager.registerBlocker(
            'ensureWinWithMulipleWilds',
            () => this.manager.isInWildWheelSequence() && !this.manager.isInWildWheelMergingSequence()
        );
    }

    public onIdle(): void {
        this.spine.spine.skeleton.setupPose();
    }

    private start(): void {
        if (!this.manager.isInWildWheelMergingSequence()) return;
        this.generateFinalMultiplier();
        this.multiplierContainer.visible = true;
        this.spine.spine.visible = true;
    }

    private end(): void {
        if (!this.manager.isInWildWheelMergingSequence()) return;
    }

    private hidden(): void {
        this.multiplierContainer.visible = false;
        this.spine.spine.visible = false;
    }

    private generateFinalMultiplier(): void {
        const multiplier = this.gameStore.get(EGameStoreKeys.SpinOutcome).steps[0].totalWinMultiplier!;
        this.multiplierText.text = `${multiplier}x`;
    }
}
